import React, { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, LineChart, Line, Cell,
} from "recharts";
import { RefreshCw, Calendar, CalendarClock, CheckCircle2, Users, UserPlus, Coins, Target, Filter, Download, Trophy, Activity, Briefcase, MapPin, Award, AlertCircle, ArrowUpRight, Zap, LineChart as IkonaGrafu } from "lucide-react";
import GradientHeader from "../components/GradientHeader.jsx";
import { Stranka, ListovaniMesice, Dlazdice, ErrorNote, Hlaska, SkeletonCards } from "../components/ui.jsx";
import {
  KartaPrehledu, HlavickaDlazdice, RadkyPrehledu, RadekPrehledu, SkupinaPrehledu,
} from "../components/PrehledDlazdice.jsx";
import { useApiData } from "../lib/api.js";
import { useDemoMode } from "../lib/DemoModeContext.jsx";
import { fmt, formatDateTime } from "../lib/format.js";

// 13.9.2026 (fáze 4f, rozhodnutí Č2): nejdelší stránka v aplikaci se skládá
// ze vzorníku. Hlavička s názvem, stavem roku v rohu ikony a akcemi, pod ní
// listování roky, čtyři hlavní dlaždice a pak šest sekcí oddělených světlým
// pruhem přes celou šířku. Nic se nesbaluje, všechno je vidět na jedno
// projetí. Čísla, atribuce, grafy i nápovědy zůstávají beze změny.

const MIN_YEAR = 2026;
const MONTH_NAMES = ["Led", "Úno", "Bře", "Dub", "Kvě", "Čer", "Čvc", "Srp", "Zář", "Říj", "Lis", "Pro"];
const MONTH_FULL = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
// Tvar "v lednu", "v červenci". Skloňování se nedá udělat přilepením písmene.
const MONTH_IN = ["lednu", "únoru", "březnu", "dubnu", "květnu", "červnu", "červenci", "srpnu", "září", "říjnu", "listopadu", "prosinci"];

// Zdroje kontaktu, stejné barvy jako Přehled
const SOURCE_COLORS = {
  "Meta Ads": "#0668E1",
  "SM Organic": "#E1306C",
  "Google": "#34A853",
  "Doporučení": "#F59E0B",
  "Web": "#6B62AC",
  "Ostatní": "#786786",
};
const PACKAGE_COLORS = ["#6E519F", "#10b981", "#f59e0b", "#3b82f6", "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#a855f7"];

// Lokality s pevnými barvami
function locationColor(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("vt") || n.includes("gym")) return "#dc2626";   // VT GYM, rudá
  if (n.includes("sport") || n.includes("hala")) return "#2563eb"; // Sportovní hala, modrá
  return "#695C74";
}

// Zkratky balíčků pro přehlednost v barovém grafu
function packageShortLabel(full) {
  const s = (full || "").toString();
  if (/permanentka/i.test(s)) return "Permanentka";
  const m = s.match(/\b(FLEXIBLE|GOLD|PREMIUM)\s*([123])T\b/i);
  if (m) {
    const letter = m[1][0].toUpperCase(); // F / G / P
    return `${letter}${m[2]}T`;
  }
  return s.length > 18 ? `${s.slice(0, 17)}…` : s;
}

// Stav roku: barva tečky v rohu ikony stránky a text po klepnutí na ni.
const STAVY = {
  current: { tecka: "ok", napoveda: "Tenhle rok právě probíhá, čísla se ještě mění." },
  past: { tecka: "neutralni", napoveda: "Rok je uzavřený, čísla se už nemění." },
  future: { tecka: "prazdna", napoveda: "Budoucí rok, data k němu zatím nejsou." },
};

// 30. 10. 2026: Online produkty, Klub FitWay40. Skloňování počtu platících členek a faktur.
function platiciClenky(n) {
  const c = Number(n) || 0;
  if (c === 0) return "zatím žádná platící členka";
  if (c === 1) return "1 platící členka, měsíčně";
  if (c >= 2 && c <= 4) return `${c} platící členky, měsíčně`;
  return `${c} platících členek, měsíčně`;
}
function pocetFaktur(n) {
  const c = Number(n) || 0;
  if (c === 1) return "1 faktura";
  if (c >= 2 && c <= 4) return `${c} faktury`;
  return `${c} faktur`;
}

// Pruh, kterým se dělí sekce stránky. Táhne se přes celou šířku, stejně jako
// světlé skupiny uvnitř karet.

function PruhSekce({ ikona: Ikona, nazev, popis }) {
  return (
    <div className="-mx-4 flex flex-wrap items-center gap-x-2 gap-y-0.5 bg-brand-light px-4 py-2.5 sm:-mx-5 sm:px-5">
      {Ikona && <Ikona className="h-4 w-4 shrink-0 text-brand-deep" />}
      <span className="text-xs font-bold uppercase tracking-wide text-brand-950">{nazev}</span>
      {popis && <span className="text-xs text-brand-deep/80">{popis}</span>}
    </div>
  );
}

// Largest-remainder method, zaručuje že součet zaokrouhlených procent je 100.
// Bez toho dává Math.round často 99 nebo 101, protože se zaokrouhluje každý
// řádek zvlášť.
function calcSharePercents(values) {
  const total = values.reduce((s, v) => s + v, 0);
  if (total === 0) return values.map(() => 0);
  const exact = values.map((v) => (v / total) * 100);
  const floored = exact.map((e) => Math.floor(e));
  const used = floored.reduce((s, v) => s + v, 0);
  const remainders = exact.map((e, i) => ({ i, frac: e - Math.floor(e) }));
  remainders.sort((a, b) => b.frac - a.frac);
  const remaining = 100 - used;
  for (let k = 0; k < remaining && k < remainders.length; k++) {
    floored[remainders[k].i]++;
  }
  return floored;
}

// Žebříček s pruhy. Hodnota je vždycky mimo pruh napravo, tmavý text na bílém
// má největší kontrast. Na telefonu je popisek nad pruhem, vedle sebe se to
// nevejde.
function BarList({ rows, colorFn, valueFormat = (v) => fmt(v), valueSuffix = "", showShare = true }) {
  const total = rows.reduce((s, r) => s + (r.value || 0), 0);
  const max = Math.max(...rows.map((r) => r.value || 0), 1);
  const sharePcts = calcSharePercents(rows.map((r) => r.value || 0));
  return (
    <div className="space-y-3">
      {rows.map((r, i) => {
        const pct = Math.round((r.value / max) * 100);
        const color = colorFn ? colorFn(r.label, i) : PACKAGE_COLORS[i % PACKAGE_COLORS.length];
        const valueText = `${valueFormat(r.value)}${valueSuffix}`;
        return (
          <div key={r.label + i}>
            <div className="mb-1 flex items-baseline justify-between gap-2">
              <span className="flex min-w-0 items-center gap-2">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                <span className="truncate text-sm font-semibold text-slate-800" title={r.label}>{r.label}</span>
              </span>
              <span className="shrink-0 whitespace-nowrap text-sm font-bold tabular-nums text-slate-900">
                {valueText}
                {showShare && total > 0 && (
                  <span className="ml-1.5 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{sharePcts[i]} %</span>
                )}
              </span>
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Dlaždice jednoho měsíce v plnění ročních cílů. Vykřičník se ukazuje jen
// u uzavřených měsíců, aktuální a budoucí vypadají neutrálně, protože je
// nejde hodnotit před koncem měsíce.
function GoalCheck({ monthKey, month, pct, goal, actual }) {
  const todayMk = new Date().toISOString().substring(0, 7); // "2026-04"
  const isClosed = monthKey < todayMk;
  const isCurrent = monthKey === todayMk;

  let styles, Icon;
  if (goal === 0) {
    styles = "bg-slate-50 border-slate-200 text-slate-400";
    Icon = Calendar;
  } else if (!isClosed) {
    styles = isCurrent
      ? "bg-sky-50 border-sky-200 text-sky-700"
      : "bg-slate-50 border-slate-200 text-slate-500";
    Icon = Calendar;
  } else if (pct >= 80) {
    styles = "bg-emerald-50 border-emerald-300 text-emerald-800";
    Icon = CheckCircle2;
  } else if (pct >= 50) {
    styles = "bg-amber-50 border-amber-300 text-amber-800";
    Icon = AlertCircle;
  } else {
    styles = "bg-rose-50 border-rose-200 text-rose-700";
    Icon = AlertCircle;
  }

  return (
    <div className={`min-w-0 rounded-xl border p-2 text-center ${styles}`}>
      <div className="text-xs font-semibold uppercase tracking-wider opacity-80">{MONTH_NAMES[month - 1]}</div>
      <Icon className="mx-auto my-1 h-4 w-4" />
      {goal > 0 ? (
        !isClosed ? (
          <>
            <div className="text-sm font-bold tabular-nums demo-sensitive">{isCurrent ? "probíhá" : "—"}</div>
            <div className="truncate text-xs tabular-nums opacity-70 demo-sensitive">cíl {fmt(goal)}</div>
          </>
        ) : (
          <>
            <div className="text-sm font-bold tabular-nums demo-sensitive">{pct} %</div>
            <div className="truncate text-xs tabular-nums opacity-70 demo-sensitive">{fmt(actual)} Kč</div>
          </>
        )
      ) : (
        <div className="text-xs opacity-60">Bez cíle</div>
      )}
    </div>
  );
}

export default function Yearly() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const [year, setYear] = useState(Math.max(currentYear, MIN_YEAR));
  const { blurred } = useDemoMode();

  const canGoPrev = year > MIN_YEAR;
  const canGoNext = year < currentYear;
  const isFuture = year > currentYear;
  const isBeforeMin = year < MIN_YEAR;

  const { data, loading, error, refreshing, refetch } = useApiData(
    (isFuture || isBeforeMin) ? null : `/api/data-yearly?year=${year}`,
  );

  const status = isFuture ? "future" : year === currentYear ? "current" : "past";
  const stav = STAVY[status];
  const handlePrint = () => window.print();

  // Funnel roku
  const leads = data?.leads || 0;
  const nqLeads = data?.nqLeads || 0;
  const trials = data?.trialCount || 0;
  const deal = data?.dealCountYear || 0;
  const loss = data?.lossCountYear || 0;
  const undecided = Math.max(0, trials - deal - loss);
  const leadToNqPct = leads > 0 ? Math.round((nqLeads / leads) * 100) : 0;
  const successPct = (deal + loss) > 0 ? Math.round((deal / (deal + loss)) * 100) : 0;

  return (
    <Stranka className={`print:print-yearly print:px-0 print:py-0 ${blurred ? "demo-blur" : ""}`}>
      <div className="print:hidden">
        <GradientHeader
          title="Roční přehled"
          icon={CalendarClock}
          loading={loading || refreshing}
          napoveda={stav.napoveda}
          tecka={stav.tecka}
          akce={[
            {
              klic: "obnovit",
              ikona: RefreshCw,
              popis: "Obnovit",
              onClick: refetch,
              disabled: loading || refreshing || isFuture || isBeforeMin,
            },
            {
              klic: "pdf",
              ikona: Download,
              popis: "Exportovat PDF",
              onClick: handlePrint,
              disabled: loading || !data,
            },
          ]}
        />
      </div>

      {/* Hlavička pro tisk */}
      <div className="hidden print:block">
        <h1 className="text-2xl font-bold text-slate-900">Roční report, {year}</h1>
        <p className="mt-1 text-sm text-slate-500">Vygenerováno: {formatDateTime(new Date())}</p>
      </div>

      <div className="print:hidden">
        <ListovaniMesice
          popis={String(year)}
          onZpet={() => setYear(year - 1)}
          onVpred={() => setYear(year + 1)}
          zpetVypnuto={!canGoPrev}
          vpredVypnuto={!canGoNext}
          naDnesek={year !== currentYear && currentYear >= MIN_YEAR ? () => setYear(currentYear) : undefined}
          popisDneska="Na letošek"
        />
      </div>

      {isBeforeMin && (
        <Hlaska ton="pozor" title="Starší rok">
          Data jsou sledovaná od roku {MIN_YEAR}, starší roky nejsou k dispozici.
        </Hlaska>
      )}
      {isFuture && (
        <Hlaska ton="pozor" title="Budoucí rok">Data k němu zatím nejsou.</Hlaska>
      )}

      {loading && !isFuture && !isBeforeMin && <SkeletonCards count={4} />}

      {error && !isFuture && !isBeforeMin && !loading && (
        <ErrorNote onRetry={refetch}>Nepodařilo se načíst data za rok {year}: {error}</ErrorNote>
      )}

      {data && !loading && !isFuture && !isBeforeMin && (
        <>
          {/* Hlavní čísla roku */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Dlazdice
              popisek="Roční obrat"
              hodnota={`${fmt(data.revenue)} Kč`}
              ton="ok"
              doplnky={<span className="text-xs text-slate-500">{data.paidCount} faktur, DPH {fmt(data.revenueVat)} Kč</span>}
            />
            <Dlazdice
              popisek="Aktivní klienti"
              hodnota={fmt(data.activeClientsYear || 0)}
              ton="znacka"
              doplnky={<span className="text-xs text-slate-500">{data.addedClients} nových, {data.endedClients} skončilo</span>}
            />
            <Dlazdice
              popisek="MRR"
              hodnota={`${fmt(data.mrr)} Kč`}
              ton="info"
              doplnky={
                <span className="text-xs text-slate-500">
                  {data.permicePotential > 0
                    ? `+ ${fmt(data.permicePotential)} Kč z permic`
                    : (data.mrrClients > 0 ? `${data.mrrClients} pravidelných` : "žádné pravidelné faktury")}
                </span>
              }
            />
            <Dlazdice
              popisek="ROI z marketingu"
              hodnota={`${data.roiPct} %`}
              ton="pozor"
              hodnotaTon={data.roiPct >= 300 ? "ok" : data.roiPct >= 150 ? "pozor" : "problem"}
              doplnky={<span className="text-xs text-slate-500">{fmt(data.mktSpend)} Kč spend</span>}
            />
          </div>

          {/* === 1. AKVIZICE === */}
          <PruhSekce ikona={UserPlus} nazev="Akvizice" popis="vrch funnelu, leady a kanály" />

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Dlazdice
              popisek="Celkem leadů"
              hodnota={fmt(data.leads)}
              ton="znacka"
              doplnky={data.peakMonth ? <span className="text-xs text-slate-500">nejvíc v {MONTH_IN[data.peakMonth - 1]}</span> : null}
            />
            <Dlazdice
              popisek="Ø CPL"
              hodnota={`${fmt(data.cpl)} Kč`}
              ton="pozor"
              doplnky={<span className="text-xs text-slate-500">MKT spend na jeden lead</span>}
            />
            <Dlazdice
              popisek="Nekvalifikované leady"
              hodnota={`${data.nqPct} %`}
              ton="problem"
              doplnky={<span className="text-xs text-slate-500">{data.nqLeads} z {data.leads}</span>}
            />
            <Dlazdice
              popisek="Sezonalita"
              hodnota={data.peakMonth ? MONTH_FULL[data.peakMonth - 1] : "—"}
              ton="info"
              doplnky={data.troughMonth ? <span className="text-xs text-slate-500">dno v {MONTH_IN[data.troughMonth - 1]}</span> : null}
            />
          </div>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={IkonaGrafu}
              nazev="Leady po měsících"
              podtitulek="sezonní trendy"
              napoveda="Rozložení leadů do jednotlivých měsíců. Ukazuje sezonní výkyvy."
            />
            <div className="p-3 sm:p-4">
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={data.monthlyBreakdown || []}>
                  <defs>
                    <linearGradient id="leadsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7C5DB0" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#7C5DB0" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE6EF" />
                  <XAxis dataKey="month" stroke="#695C74" fontSize={13} tickFormatter={(m) => MONTH_NAMES[m - 1]} />
                  <YAxis stroke="#695C74" fontSize={13} />
                  <Tooltip labelFormatter={(m) => `${MONTH_FULL[m - 1]} ${year}`} />
                  <Area type="monotone" dataKey="leads" stroke="#7C5DB0" strokeWidth={2} fill="url(#leadsGrad)" name="Leady" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </KartaPrehledu>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={Filter}
              nazev="Zdroje kontaktu"
              podtitulek="odkud leady chodí"
              napoveda="Rozdělení leadů podle zdroje. Normalizace je stejná jako v Přehledu."
            />
            <div className="p-3 sm:p-4">
              {Object.keys(data.sourcesByYear || {}).length > 0 ? (
                <BarList
                  rows={Object.entries(data.sourcesByYear)
                    .map(([label, value]) => ({ label, value }))
                    .sort((a, b) => b.value - a.value)}
                  colorFn={(label) => SOURCE_COLORS[label] || "#786786"}
                  valueFormat={(v) => `${v}`}
                />
              ) : <div className="py-8 text-center text-sm text-slate-400">Žádná data</div>}
            </div>
          </KartaPrehledu>

          {/* === 2. KONVERZE === */}
          <PruhSekce ikona={ArrowUpRight} nazev="Konverze" popis="jak leady postupují funnelem" />

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={Filter}
              nazev="Funnel roku"
              podtitulek="od leadu k podpisu"
              odznak={`${fmt(leads)} leadů`}
              napoveda="Cesta leadů k uzavřenému dealu za celý rok. Úspěšnost je Deal dělený součtem Deal a Prohra."
            />
            <RadkyPrehledu>
              <RadekPrehledu popisek="Leady" hodnota={fmt(leads)} />
              {nqLeads > 0 && (
                <RadekPrehledu
                  popisek="Nekvalifikované leady"
                  napoveda="Leady se stagem NQLead, tedy ty, ze kterých se trial nedomlouvá."
                  hodnota={fmt(nqLeads)}
                  doplnek={`${leadToNqPct} % z leadů`}
                  tonHodnoty="text-rose-700"
                />
              )}
              <RadekPrehledu
                popisek="Zaplacené trialy"
                hodnota={fmt(trials)}
                doplnek={`${data.leadToTrialPct} % z leadů`}
              />
            </RadkyPrehledu>

            <SkupinaPrehledu nazev="Jak trialy dopadly" />
            <RadkyPrehledu>
              <RadekPrehledu
                popisek="Deal"
                hodnota={fmt(deal)}
                doplnek={`${data.trialToDealPct} % z trialů`}
                tonHodnoty="text-emerald-700"
              />
              <RadekPrehledu popisek="Prohra" hodnota={fmt(loss)} tonHodnoty="text-rose-700" />
              <RadekPrehledu popisek="Nerozhodnuto" hodnota={fmt(undecided)} />
              {(deal > 0 || loss > 0) && (
                <RadekPrehledu
                  popisek="Úspěšnost rozhodnutých"
                  napoveda="Deal dělený součtem Deal a Prohra. Nerozhodnuté se do toho nepočítají."
                  hodnota={`${successPct} %`}
                  tonHodnoty={successPct >= 70 ? "text-emerald-700" : successPct >= 50 ? "text-amber-700" : "text-rose-700"}
                />
              )}
            </RadkyPrehledu>
          </KartaPrehledu>

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            <Dlazdice
              popisek="Lead na trial"
              hodnota={`${data.leadToTrialPct} %`}
              ton="pozor"
              doplnky={<span className="text-xs text-slate-500">{data.trialCount} z {data.leads}</span>}
            />
            <Dlazdice
              popisek="Trial na deal"
              hodnota={`${data.trialToDealPct} %`}
              ton="ok"
              doplnky={<span className="text-xs text-slate-500">{data.dealCountYear} z {data.trialCount}</span>}
            />
            <Dlazdice
              popisek="Lead na deal"
              hodnota={`${data.leadToDealPct} %`}
              ton="znacka"
              doplnky={<span className="text-xs text-slate-500">{data.dealCountYear} z {data.leads}</span>}
            />
          </div>

          {/* === 3. REVENUE === */}
          <PruhSekce ikona={Coins} nazev="Revenue" popis="peníze, balíčky, online produkty a top klienti" />

          {/* 30. 10. 2026: Online produkty, Klub FitWay40 (samostatně, mimo osobní tréninky) */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            <Dlazdice
              popisek="Obrat z osobních tréninků"
              hodnota={`${fmt(data.revenue - data.trialRevenue - (data.onlineRevenue || 0))} Kč`}
              ton="ok"
              doplnky={<span className="text-xs text-slate-500">DPH {fmt(data.packagesRevenueVat || 0)} Kč</span>}
            />
            <Dlazdice
              popisek="Obrat z trialů"
              hodnota={`${fmt(data.trialRevenue)} Kč`}
              ton="info"
              doplnky={<span className="text-xs text-slate-500">{data.trialCount} zaplacených, {data.trialRevenueRatio} % obratu</span>}
            />
            <Dlazdice
              popisek="Online produkty"
              hodnota={`${fmt(data.onlineRevenue || 0)} Kč`}
              ton="znacka"
              doplnky={<span className="text-xs text-slate-500">Klub FitWay40, {pocetFaktur(data.onlineCount)}, DPH {fmt(data.onlineRevenueVat || 0)} Kč</span>}
            />
            <Dlazdice
              popisek="MRR Klubu"
              hodnota={data.mrrKlub == null ? "nedostupné" : `${fmt(data.mrrKlub)} Kč`}
              ton="info"
              doplnky={<span className="text-xs text-slate-500">{data.mrrKlub == null ? "nepodařilo se načíst" : platiciClenky(data.klubClenek)}</span>}
            />
            <Dlazdice
              popisek="Ø hodnota dealu"
              hodnota={`${fmt(data.avgDealValue)} Kč`}
              ton="znacka"
              doplnky={<span className="text-xs text-slate-500">první faktura nového klienta</span>}
            />
            <Dlazdice
              popisek="Ø růst měsíc na měsíc"
              hodnota={data.momGrowthAvg !== null && data.momGrowthAvg !== undefined
                ? `${data.momGrowthAvg >= 0 ? "+" : ""}${data.momGrowthAvg} %`
                : "—"}
              ton="pozor"
              hodnotaTon={data.momGrowthAvg >= 0 ? "ok" : "problem"}
              doplnky={<span className="text-xs text-slate-500">přes uzavřené měsíce</span>}
            />
          </div>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={IkonaGrafu}
              nazev="Měsíční obrat"
              podtitulek="a růst proti předchozímu měsíci"
              napoveda="Měsíční obrat z FAPI s DPH. Zelený sloupec znamená růst proti předchozímu měsíci, oranžový pokles."
            />
            <div className="p-3 sm:p-4">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.monthlyBreakdown || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE6EF" />
                  <XAxis dataKey="month" stroke="#695C74" fontSize={13} tickFormatter={(m) => MONTH_NAMES[m - 1]} />
                  <YAxis stroke="#695C74" fontSize={13} tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
                  <Tooltip
                    formatter={(v) => [`${fmt(v)} Kč`, "Obrat"]}
                    labelFormatter={(m) => `${MONTH_FULL[m - 1]} ${year}`}
                  />
                  <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                    {(data.monthlyBreakdown || []).map((m, i) => (
                      <Cell key={i} fill={m.momGrowthPct === null ? "#10b981" : m.momGrowthPct >= 0 ? "#10b981" : "#f59e0b"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </KartaPrehledu>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={Award}
              nazev="Obrat podle balíčku"
              napoveda="Rozdělení obratu podle typu balíčku. Zkratky: F1T a F2T je FLEXIBLE, G1T až G3T je GOLD, P1T až P3T je PREMIUM. Položky mimo známé balíčky spadnou do Ostatní. Zdroj jsou položky FAPI faktur."
            />
            <div className="p-3 sm:p-4">
              {(data.packageBreakdown || []).length > 0 ? (
                <BarList
                  rows={(data.packageBreakdown || []).map((p) => ({ label: packageShortLabel(p.label), value: p.amount }))}
                  colorFn={(label, i) => label === "Ostatní" ? "#786786" : PACKAGE_COLORS[i % PACKAGE_COLORS.length]}
                  valueFormat={(v) => `${fmt(v)} Kč`}
                />
              ) : <div className="py-8 text-center text-sm text-slate-400">Žádná data</div>}
            </div>
          </KartaPrehledu>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={Trophy}
              nazev="Top 10 klientů"
              podtitulek="podle obratu za rok"
              napoveda="Klienti s největšími zaplacenými fakturami za rok, s DPH. Ukazuje, kdo táhne příjmy."
            />
            <div className="p-3 sm:p-4">
              {(data.topClients || []).length > 0 ? (
                <div className="space-y-3">
                  {data.topClients.map((c, i) => {
                    const maxAmount = data.topClients[0].amount;
                    const pct = Math.round((c.amount / maxAmount) * 100);
                    const medaile = i === 0 ? "bg-brand-gold text-brand-dark"
                      : i === 1 ? "bg-slate-300 text-slate-800"
                      : i === 2 ? "bg-amber-700 text-white"
                      : "bg-slate-100 text-slate-600";
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${medaile}`}>
                          {i + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="mb-1 flex items-baseline justify-between gap-2">
                            <span className="truncate text-sm font-semibold text-slate-900 demo-sensitive" title={c.name}>{c.name}</span>
                            <span className="shrink-0 text-sm font-bold tabular-nums text-emerald-700">{fmt(c.amount)} Kč</span>
                          </div>
                          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                            <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : <div className="py-8 text-center text-sm text-slate-400">Žádná data</div>}
            </div>
          </KartaPrehledu>

          {/* === 4. KLIENTSKÁ ZÁKLADNA === */}
          <PruhSekce ikona={Users} nazev="Klientská základna" popis="distribuce, churn a délka spolupráce" />

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Dlazdice
              popisek="Čistý růst za rok"
              hodnota={`${data.addedClients - data.endedClients > 0 ? "+" : ""}${data.addedClients - data.endedClients}`}
              ton="ok"
              hodnotaTon={data.addedClients - data.endedClients > 0 ? "ok" : "problem"}
              doplnky={<span className="text-xs text-slate-500">{data.addedClients} přibylo, {data.endedClients} skončilo</span>}
            />
            <Dlazdice
              popisek="Churn"
              hodnota={`${data.churnRate} %`}
              ton="problem"
              hodnotaTon={data.churnRate < 10 ? "ok" : data.churnRate < 20 ? "pozor" : "problem"}
              doplnky={<span className="text-xs text-slate-500">zdravé je pod 10 %</span>}
            />
            <Dlazdice
              popisek="Ø délka spolupráce"
              hodnota={data.avgCooperationMonths > 0 ? `${data.avgCooperationMonths} měs.` : "—"}
              ton="znacka"
              doplnky={<span className="text-xs text-slate-500">u ukončených klientů</span>}
            />
            <Dlazdice
              popisek="Ø měsíční fee"
              hodnota={`${fmt(data.avgMonthlyFee)} Kč`}
              ton="info"
              doplnky={<span className="text-xs text-slate-500">u aktivních klientů</span>}
            />
          </div>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={Briefcase}
              nazev="Klienti podle trenéra"
              podtitulek="zatížení a kapacita"
              napoveda="Kolik aktivních klientů má každý trenér."
            />
            <div className="p-3 sm:p-4">
              {Object.keys(data.trainerDistribution || {}).length > 0 ? (
                <BarList
                  rows={Object.entries(data.trainerDistribution)
                    .map(([label, value]) => ({ label, value }))
                    .sort((a, b) => b.value - a.value)}
                  colorFn={() => "#7C5DB0"}
                  valueFormat={(v) => `${v}`}
                />
              ) : <div className="py-8 text-center text-sm text-slate-400">Žádná data</div>}
            </div>
          </KartaPrehledu>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={MapPin}
              nazev="Klienti podle lokality"
              napoveda="Rozdělení aktivních klientů podle provozovny. VT GYM rudá, Sportovní hala modrá."
            />
            <div className="p-3 sm:p-4">
              {Object.keys(data.locationDistribution || {}).length > 0 ? (
                <BarList
                  rows={Object.entries(data.locationDistribution)
                    .map(([label, value]) => ({ label, value }))
                    .sort((a, b) => b.value - a.value)}
                  colorFn={(label) => locationColor(label)}
                  valueFormat={(v) => `${v}`}
                />
              ) : <div className="py-8 text-center text-sm text-slate-400">Žádná data</div>}
            </div>
          </KartaPrehledu>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={Award}
              nazev="Balíčky aktivních klientů"
              napoveda="Rozdělení aktivních klientů podle typu balíčku. Zdroj jsou karty klientů."
            />
            <div className="p-3 sm:p-4">
              {Object.keys(data.packageDistribution || {}).length > 0 ? (
                <BarList
                  rows={Object.entries(data.packageDistribution)
                    .map(([label, value]) => ({ label, value }))
                    .sort((a, b) => b.value - a.value)}
                  colorFn={(_, i) => PACKAGE_COLORS[i % PACKAGE_COLORS.length]}
                  valueFormat={(v) => `${v}`}
                />
              ) : <div className="py-8 text-center text-sm text-slate-400">Žádná data</div>}
            </div>
          </KartaPrehledu>

          {/* === 5. MARKETING EFEKTIVITA === */}
          <PruhSekce ikona={Zap} nazev="Marketing efektivita" popis="CAC, LTV a poměr růstu" />

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Dlazdice
              popisek="MKT spend"
              hodnota={`${fmt(data.mktSpend)} Kč`}
              ton="pozor"
              doplnky={<span className="text-xs text-slate-500">za celý rok</span>}
            />
            <Dlazdice
              popisek="CAC"
              hodnota={`${fmt(data.cac)} Kč`}
              ton="znacka"
              doplnky={<span className="text-xs text-slate-500">{data.addedClients} nových klientů</span>}
            />
            <Dlazdice
              popisek="LTV"
              hodnota={`${fmt(data.ltv)} Kč`}
              ton="ok"
              doplnky={<span className="text-xs text-slate-500">fee krát {data.avgCooperationMonths || 12} měsíců</span>}
            />
            <Dlazdice
              popisek="LTV ku CAC"
              hodnota={String(data.ltvCacRatio)}
              ton="info"
              hodnotaTon={data.ltvCacRatio >= 3 ? "ok" : data.ltvCacRatio >= 1.5 ? "pozor" : "problem"}
              doplnky={<span className="text-xs text-slate-500">zdravé je nad 3</span>}
            />
          </div>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={IkonaGrafu}
              nazev="Cena za lead po měsících"
              podtitulek="klesající křivka je dobře"
              napoveda="Vývoj nákladu na jeden lead v jednotlivých měsících. Klesající znamená efektivnější marketing."
            />
            <div className="p-3 sm:p-4">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={(data.monthlyBreakdown || []).map((m) => ({ ...m, cpl: m.leads > 0 ? Math.round(m.mktSpend / m.leads) : 0 }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE6EF" />
                  <XAxis dataKey="month" stroke="#695C74" fontSize={13} tickFormatter={(m) => MONTH_NAMES[m - 1]} />
                  <YAxis stroke="#695C74" fontSize={13} tickFormatter={(v) => `${v} Kč`} />
                  <Tooltip formatter={(v) => `${fmt(v)} Kč`} labelFormatter={(m) => `${MONTH_FULL[m - 1]} ${year}`} />
                  <Line type="monotone" dataKey="cpl" stroke="#f59e0b" strokeWidth={3} dot={{ r: 5, fill: "#f59e0b" }} name="CPL" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </KartaPrehledu>

          {/* === 6. PLNĚNÍ CÍLŮ === */}
          <PruhSekce ikona={Target} nazev="Plnění ročních cílů" popis="měsíc po měsíci" />

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={Target}
              nazev="Cíle proti skutečnosti"
              podtitulek="jen uzavřené měsíce"
              napoveda="Hodnotí se jen uzavřené měsíce před aktuálním. Zelená od 80 %, žlutá od 50 %, červená pod 50 %. Aktuální měsíc probíhá, budoucí jsou šedé."
            />
            <div className="grid grid-cols-3 gap-2 p-3 sm:grid-cols-6 sm:p-4 lg:grid-cols-12">
              {(data.goalsByMonth || []).map((g) => (
                <GoalCheck key={g.monthKey} monthKey={g.monthKey} month={g.month} pct={g.pct} goal={g.goal} actual={g.actual} />
              ))}
            </div>
            <RadkyPrehledu>
              <RadekPrehledu
                popisek="Splněných měsíců"
                hodnota={`${data.monthsMet} z ${data.monthsWithGoal}`}
              />
              {data.yearlyGoalClosed > 0 && (
                <RadekPrehledu
                  popisek="Plnění uzavřených měsíců"
                  hodnota={`${data.yearlyGoalPctClosed || 0} %`}
                  doplnek={`z ${fmt(data.yearlyGoalClosed)} Kč`}
                  tonHodnoty={(data.yearlyGoalPctClosed || 0) >= 80 ? "text-emerald-700" : (data.yearlyGoalPctClosed || 0) >= 50 ? "text-amber-700" : "text-rose-700"}
                />
              )}
            </RadkyPrehledu>
          </KartaPrehledu>

          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={Activity}
              nazev="Detail po měsících"
              napoveda="Rozpad klíčových metrik po měsících, obrat s DPH. Sloupec MoM ukazuje meziměsíční růst jen u uzavřených měsíců, aktuální a budoucí ho mají prázdný."
            />
            <div className="p-3 sm:p-4">
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">
                      <th className="px-3 py-2.5 text-left font-semibold">Měsíc</th>
                      <th className="px-3 py-2.5 text-right font-semibold">Obrat</th>
                      <th className="hidden px-3 py-2.5 text-right font-semibold sm:table-cell">MoM</th>
                      <th className="hidden px-3 py-2.5 text-right font-semibold md:table-cell">Leady</th>
                      <th className="hidden px-3 py-2.5 text-right font-semibold md:table-cell">Trialů</th>
                      <th className="px-3 py-2.5 text-right font-semibold">Noví</th>
                      <th className="hidden px-3 py-2.5 text-right font-semibold sm:table-cell">Skončili</th>
                      <th className="hidden px-3 py-2.5 text-right font-semibold lg:table-cell">MKT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.monthlyBreakdown || []).map((m, idx) => (
                      <tr key={m.monthKey} className={`border-b border-slate-100 ${idx % 2 === 1 ? "bg-slate-50/40" : ""}`}>
                        <td className="px-3 py-2.5 font-semibold text-slate-900">{MONTH_FULL[m.month - 1]}</td>
                        <td className="px-3 py-2.5 text-right font-bold tabular-nums text-emerald-700">
                          {m.revenue > 0 ? `${fmt(m.revenue)} Kč` : "—"}
                        </td>
                        <td className="hidden px-3 py-2.5 text-right tabular-nums sm:table-cell">
                          {m.momGrowthPct !== null && m.momGrowthPct !== undefined ? (
                            <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${m.momGrowthPct >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                              {m.momGrowthPct >= 0 ? "+" : ""}{m.momGrowthPct} %
                            </span>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="hidden px-3 py-2.5 text-right tabular-nums text-slate-700 md:table-cell">{m.leads || "—"}</td>
                        <td className="hidden px-3 py-2.5 text-right tabular-nums text-slate-700 md:table-cell">{m.trialCount || "—"}</td>
                        <td className="px-3 py-2.5 text-right font-bold tabular-nums text-brand-deep">{m.addedClients || "—"}</td>
                        <td className="hidden px-3 py-2.5 text-right tabular-nums text-rose-600 sm:table-cell">{m.endedClients || "—"}</td>
                        <td className="hidden px-3 py-2.5 text-right tabular-nums text-amber-700 lg:table-cell">
                          {m.mktSpend > 0 ? `${fmt(m.mktSpend)} Kč` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </KartaPrehledu>
        </>
      )}
    </Stranka>
  );
}
